/**
 * Auth Keeper — mantem a sessao viva em PWA.
 *
 * Por que isso existe:
 *  - Em PWA iOS standalone, o WebKit usa um particionamento de storage e tem
 *    eviction agressivo de cookies de sessao. JWT em localStorage sobrevive
 *    melhor — mas se o access token expira durante uma sessao longa offline,
 *    a primeira request volta como 401. O interceptor do axios ja tenta
 *    refresh, mas isso vira corrida com render. Solucao: refrescar antes,
 *    proativamente.
 *
 * Quando chamamos restoreSession():
 *  1. Boot do app (AuthProvider mount) — restore na home.
 *  2. Cada mudanca de rota (re-rodar o keeper por rota).
 *  3. Visibilitychange para "visible" + pageshow (PWA voltando do background).
 *  4. online (voltou rede).
 *
 * O que faz:
 *  - Se nao ha refresh_token: noop.
 *  - Se o access token ainda tem >5 min de vida: noop (decoded.exp).
 *  - Caso contrario: POST /refresh com o refresh_token, grava novo access em
 *    localStorage. Em falha 401, limpa storage e devolve {ok:false} para o
 *    chamador decidir redirect.
 *
 * Dedup: uma unica promise inflight por vez.
 */

import * as sessao from '../platform/storage';
import api from '../api';
import { logPwaEvent } from './telemetry';

const REFRESH_THRESHOLD_S = 5 * 60; // refrescar se faltam menos de 5 min
let inflight = null;

function decodeJwt(token) {
  try {
    const part = token.split('.')[1];
    const padded = part.padEnd(part.length + (4 - (part.length % 4)) % 4, '=');
    const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch {
    return null;
  }
}

function needsRefresh() {
  const access = sessao.getItem('token');
  if (!access) return true; // ha refresh mas nao ha access → restore
  const claims = decodeJwt(access);
  if (!claims || !claims.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return claims.exp - now <= REFRESH_THRESHOLD_S;
}

export async function restoreSession({ force = false, reason = 'manual' } = {}) {
  // No nativo o storage precisa estar em memoria antes de qualquer leitura.
  // hydrate() e idempotente e no-op na web, entao chamar aqui apenas garante
  // que a ordem de boot (App.js x AuthContext) deixe de importar.
  await sessao.hydrate();

  const refresh = sessao.getItem('refresh_token');
  if (!refresh) {
    return { ok: false, reason: 'no-refresh-token' };
  }
  if (!force && !needsRefresh()) {
    return { ok: true, reason: 'fresh' };
  }
  if (inflight) return inflight;

  const started = performance.now();
  inflight = api
    .post('/refresh', {}, {
      headers: { Authorization: `Bearer ${refresh}` },
      // marca para o interceptor nao tentar refresh recursivo
      _retry: true,
    })
    .then((res) => {
      const token = res.data?.access_token;
      if (token) {
        sessao.setItem('token', token);
        logPwaEvent('auth-restore-ok', {
          reason,
          tookMs: Math.round(performance.now() - started),
        });
        return { ok: true, reason: 'refreshed' };
      }
      return { ok: false, reason: 'no-token-in-response' };
    })
    .catch((err) => {
      const status = err?.response?.status;
      logPwaEvent('auth-restore-fail', {
        reason,
        status,
        msg: err?.message,
        tookMs: Math.round(performance.now() - started),
      });
      // 401/403 no /refresh → refresh token invalido. Limpa e devolve.
      if (status === 401 || status === 403) {
        sessao.removeItem('token');
        sessao.removeItem('refresh_token');
      }
      return { ok: false, reason: `error-${status || 'network'}` };
    })
    .finally(() => { inflight = null; });

  return inflight;
}

let listenersInstalled = false;

/**
 * Instala listeners globais que disparam restoreSession() em momentos chave.
 * Idempotente — chamar uma unica vez no boot do app.
 */
export function installAuthKeeperListeners() {
  if (listenersInstalled || typeof window === 'undefined') return;
  listenersInstalled = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      restoreSession({ reason: 'visibilitychange' });
    }
  });

  window.addEventListener('pageshow', (e) => {
    // pageshow.persisted = true → veio do bfcache (typico em iOS PWA).
    if (e.persisted) restoreSession({ force: true, reason: 'bfcache' });
  });

  window.addEventListener('online', () => {
    restoreSession({ reason: 'online' });
  });

  // Storage sync entre abas — outra aba pode ter feito logout/login.
  window.addEventListener('storage', (e) => {
    if (e.key === 'token' && !e.newValue) {
      logPwaEvent('storage-token-cleared', { from: 'other-tab' });
    }
  });
}
